/**
 * シナリオ生成画面側の「収録動画から」(docs/features/video-import-scenario-key-points-plan.md §5)。
 *
 * 動画エディタから遷移の state で受け取った選択 (SourceVideoSelection: 整理済みの要点と話題) を持ち、利用者が絞り込んだ
 * 要点 (pointIds) と想定尺を生成 payload の `source_video: {project_id, digest_id, point_ids, duration_sec}` に付ける。
 * 材料の組み立てと固定はサーバー (受付) が行う。
 */
import { suggestedPointIds, type SourceVideoSelection } from '../../../features/videoEditor/sourceVideo';

export interface SourceVideoState extends SourceVideoSelection {
  /** 選んだ要点 (digest.points の id)。既定は想定尺に応じた上位 N 件 */
  pointIds: string[];
  /** 利用者がチェックを触ったか。触るまでは想定尺を変えると既定を入れ直す */
  userTouched: boolean;
  /** 生成 payload に付ける想定尺 (秒)。ScenarioGenerator が想定尺の state から同期する */
  durationSec: number;
}

/** 遷移の state のキー。動画エディタの「この動画からシナリオを作る」が入れる */
export interface SourceVideoLocationState {
  sourceVideo?: SourceVideoSelection;
}

export const SOURCE_VIDEO_DEFAULT_DURATION_SEC = 30;

export function readSourceVideoState(state: unknown): SourceVideoSelection | null {
  const sv = (state as SourceVideoLocationState | null)?.sourceVideo;
  if (!sv || typeof sv !== 'object') return null;
  if (typeof sv.projectId !== 'string' || !sv.digest || typeof sv.digest.id !== 'string' || !Array.isArray(sv.digest.points) || sv.digest.points.length === 0) return null;
  return { ...sv, productId: sv.productId ?? null, asin: sv.asin ?? null };
}

/** 遷移の選択から画面の state を作る (既定 = 想定尺に応じた上位 N 件)。 */
export function initialSourceVideoState(selection: SourceVideoSelection, durationSec: number = SOURCE_VIDEO_DEFAULT_DURATION_SEC): SourceVideoState {
  return { ...selection, pointIds: suggestedPointIds(selection.digest, durationSec), userTouched: false, durationSec };
}

/** 想定尺の変更を同期する。利用者が触っていなければ既定を入れ直す (§5.2)。変化が無ければ同じ参照を返す */
export function syncSourceVideoDuration(state: SourceVideoState, durationSec: number): SourceVideoState {
  if (state.durationSec === durationSec) return state;
  return { ...state, durationSec, pointIds: state.userTouched ? state.pointIds : suggestedPointIds(state.digest, durationSec) };
}

/** 想定尺の値 (option key かラベル) から秒を読む。読めなければ null */
export function durationSecondsOf(duration: string | null | undefined, options: ReadonlyArray<{ value: string; label: string }>): number | null {
  if (!duration) return null;
  const label = options.find((o) => o.value === duration)?.label ?? duration;
  const m = /(\d+)/.exec(label);
  return m ? Number(m[1]) : null;
}

/** 生成 payload に source_video を付ける (無ければそのまま)。 */
export function applySourceVideoToPayload<T extends Record<string, unknown>>(payload: T, sourceVideo: SourceVideoState | null | undefined): T {
  if (!sourceVideo) return payload;
  return {
    ...payload,
    source_video: {
      project_id: sourceVideo.projectId,
      digest_id: sourceVideo.digest.id,
      point_ids: sourceVideo.pointIds,
      duration_sec: sourceVideo.durationSec,
    },
  };
}

/** 保存済みの generation_metadata.source_video (BE の metadata_entry)。生成後の表示はこれだけを読む。 */
export interface ScenarioSourceVideoMeta {
  project_id?: string | null;
  project_name?: string | null;
  label?: string | null;
  import_version?: number | null;
  digest_id?: string | null;
  duration_sec?: number | null;
  point_ids?: string[];
  suggested_point_ids?: string[];
  points?: Array<{ id: string; text: string; rank?: number | null }>;
  segment_ids?: string[];
  /** 旧形式 (訴求) のシナリオ */
  appeals?: string[];
  brief?: string | null;
  chars?: number | null;
  omitted?: string[];
  segment_count?: number | null;
  total_segments?: number | null;
  builtAt?: string | null;
}

export function readSourceVideoMeta(meta: Record<string, unknown> | null | undefined): ScenarioSourceVideoMeta | null {
  const raw = meta?.source_video;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const sv = raw as ScenarioSourceVideoMeta;
  if (typeof sv.brief !== 'string' && typeof sv.project_id !== 'string') return null;
  return sv;
}

/** 由来セクションの要点の行: 選んだ要点 (重要度順) と、AI の提案との差 (スナップショットから引く)。 */
export function describeSourceVideoPoints(meta: ScenarioSourceVideoMeta): { selected: string[]; removed: string[]; added: string[] } {
  const byId = new Map((meta.points ?? []).map((p) => [p.id, p]));
  const text = (id: string) => byId.get(id)?.text ?? id;
  const selectedIds = [...(meta.point_ids ?? [])].sort((a, b) => (byId.get(a)?.rank ?? 0) - (byId.get(b)?.rank ?? 0));
  const suggested = new Set(meta.suggested_point_ids ?? []);
  const selected = new Set(selectedIds);
  return {
    selected: selectedIds.map(text),
    removed: [...suggested].filter((id) => !selected.has(id)).map(text),
    added: selectedIds.filter((id) => !suggested.has(id)).map(text),
  };
}

/** 設定行のサマリに出す文言。 */
export function sourceVideoSummaryLabel(sourceVideo: SourceVideoState): string {
  return `収録動画: ${sourceVideo.label}（${sourceVideo.pointIds.length} / ${sourceVideo.digest.points.length}要点）`;
}
